import React from 'react';
import { Camera, ChevronRight, Key, Lightbulb, Plus, Rocket, Sparkles, X } from 'lucide-react';

type CountingModalMode = 'manual' | 'auto';
type AutocountModalTab = 'components' | 'lighting';

type QueuedComponent = {
  name: string;
  color: string;
  referenceImage: string;
};

export interface NewComponentCountModalProps {
  show: boolean;
  isAddingNewOnly: boolean;
  countingModalMode: CountingModalMode;
  newComponentName: string;
  newComponentColor: string;
  isAddingComponent: boolean;
  quickCountSearch: string;
  selectedComponentsForBatch: Set<string>;
  referenceAreaImage: string | null;
  electricalKey: any;
  autocountModalTab: AutocountModalTab;
  onOverlayClose: () => void;
  onCloseButton: () => void;
  onStartLightingScheduleExtraction: () => void;
  onStartQuickCount: () => void;
  onBatchAutocount: () => void;
  onRetakeAreaSelection: () => void;
  onStartAreaSelection: () => void;
  onConfirmAddToKey: () => void;
  onKeyExtracted?: (key: any) => void;
  setIsAddingNewOnly: React.Dispatch<React.SetStateAction<boolean>>;
  setNewComponentName: React.Dispatch<React.SetStateAction<string>>;
  setNewComponentColor: React.Dispatch<React.SetStateAction<string>>;
  setQuickCountSearch: React.Dispatch<React.SetStateAction<string>>;
  setSelectedComponentsForBatch: React.Dispatch<React.SetStateAction<Set<string>>>;
  setAutocountModalTab: React.Dispatch<React.SetStateAction<AutocountModalTab>>;
}

export const NewComponentCountModal: React.FC<NewComponentCountModalProps> = ({
  show,
  isAddingNewOnly,
  countingModalMode,
  newComponentName,
  newComponentColor,
  isAddingComponent,
  quickCountSearch,
  selectedComponentsForBatch,
  referenceAreaImage,
  electricalKey,
  autocountModalTab,
  onOverlayClose,
  onCloseButton,
  onStartLightingScheduleExtraction,
  onStartQuickCount,
  onBatchAutocount,
  onRetakeAreaSelection,
  onStartAreaSelection,
  onConfirmAddToKey,
  onKeyExtracted,
  setIsAddingNewOnly,
  setNewComponentName,
  setNewComponentColor,
  setQuickCountSearch,
  setSelectedComponentsForBatch,
  setAutocountModalTab,
}) => {
  if (!show) return null;

  return (
    <div
      className="new-component-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100001,
      }}
      onClick={onOverlayClose}
    >
      <div
        className="new-component-modal"
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          width: '90vw',
          maxWidth: isAddingNewOnly ? '400px' : '760px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
          border: '1px solid var(--border-color)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: 600 }}>
            {isAddingNewOnly ? 'Add New Component' : (countingModalMode === 'manual' ? (newComponentName ? 'Add to Key' : 'Manual Count') : 'AI Autocount')}
          </h3>
          <button
            onClick={onCloseButton}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {countingModalMode === 'auto' && !isAddingNewOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', flexShrink: 0 }}>
            <button
              onClick={onStartLightingScheduleExtraction}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
              }}
            >
              <Lightbulb size={16} />
              Extract Lighting Schedule
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {!isAddingNewOnly && electricalKey && electricalKey.components && (() => {
            const allAvailable = countingModalMode === 'auto'
              ? electricalKey.components.filter((c: any) => c.referenceImage || c.symbolType === 'Lighting' || c.componentType === 'Switches')
              : electricalKey.components;

            const regularComponents = allAvailable.filter((c: any) => (c.symbolType || '') !== 'Lighting');
            const lightingComponents = allAvailable.filter((c: any) => c.symbolType === 'Lighting');
            const availableComponents = autocountModalTab === 'lighting' ? lightingComponents : regularComponents;

            const filteredComponents = availableComponents.filter((c: any) => {
              const q = quickCountSearch.toLowerCase();
              return c.name.toLowerCase().includes(q) ||
                (c.symbol && c.symbol.toLowerCase().includes(q)) ||
                (c.description && c.description.toLowerCase().includes(q));
            });

            return (regularComponents.length > 0 || lightingComponents.length > 0) ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  marginBottom: '8px',
                  fontWeight: 500,
                  flexShrink: 0,
                }}>
                  <Sparkles size={14} style={{ color: '#3b82f6' }} />
                  Existing Components
                </label>

                <div style={{
                  display: 'flex',
                  gap: '0px',
                  marginBottom: '8px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  padding: '3px',
                  flexShrink: 0,
                }}>
                  <button
                    onClick={() => { setAutocountModalTab('components'); setSelectedComponentsForBatch(new Set<string>()); }}
                    style={{
                      flex: 1,
                      padding: '7px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      background: autocountModalTab === 'components' ? 'var(--bg-primary)' : 'transparent',
                      color: autocountModalTab === 'components' ? 'var(--text-primary)' : 'var(--text-muted)',
                      boxShadow: autocountModalTab === 'components' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    <Key size={13} />
                    Power ({regularComponents.length})
                  </button>
                  <button
                    onClick={() => { setAutocountModalTab('lighting'); setSelectedComponentsForBatch(new Set<string>()); }}
                    style={{
                      flex: 1,
                      padding: '7px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      background: autocountModalTab === 'lighting' ? 'var(--bg-primary)' : 'transparent',
                      color: autocountModalTab === 'lighting' ? 'var(--text-primary)' : 'var(--text-muted)',
                      boxShadow: autocountModalTab === 'lighting' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    <Lightbulb size={13} />
                    Lighting ({lightingComponents.length})
                  </button>
                </div>

                <div style={{ marginBottom: '8px', flexShrink: 0 }}>
                  <input
                    type="text"
                    value={quickCountSearch}
                    onChange={(e) => setQuickCountSearch(e.target.value)}
                    placeholder="Search components..."
                    style={{
                      width: '100%',
                      padding: '8px 12px 8px 36px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Ccircle cx=\'11\' cy=\'11\' r=\'8\'%3E%3C/circle%3E%3Cpath d=\'m21 21-4.35-4.35\'%3E%3C/path%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: '12px center',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.background = 'var(--bg-primary)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-strong)';
                      e.currentTarget.style.background = 'var(--bg-secondary)';
                    }}
                  />
                </div>

                {countingModalMode === 'auto' && filteredComponents.length > 0 && (() => {
                  const allSelected = selectedComponentsForBatch.size === filteredComponents.length && filteredComponents.length > 0;
                  return (
                    <button
                      onClick={() => {
                        if (allSelected) {
                          setSelectedComponentsForBatch(new Set<string>());
                        } else {
                          const allKeys = new Set<string>(filteredComponents.map((c: any) =>
                            c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name,
                          ));
                          setSelectedComponentsForBatch(allKeys);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        marginBottom: '8px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transition: 'all 0.2s',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-primary)';
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.color = '#3b82f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                        e.currentTarget.style.borderColor = 'var(--border-strong)';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        readOnly
                        style={{ marginRight: '4px' }}
                      />
                      {allSelected ? `Deselect All (${filteredComponents.length})` : `Select All (${filteredComponents.length})`}
                    </button>
                  );
                })()}

                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  minHeight: 0,
                }}>
                  {filteredComponents.length > 0 ? (
                    filteredComponents.map((component: any, idx: number) => {
                      const selectionKey = component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name;
                      return (
                        <div
                          key={selectionKey + '-' + idx}
                          onClick={() => {
                            if (countingModalMode === 'auto') {
                              setSelectedComponentsForBatch(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(selectionKey)) {
                                  newSet.delete(selectionKey);
                                } else {
                                  newSet.add(selectionKey);
                                }
                                return newSet;
                              });
                            } else {
                              setNewComponentName(component.name);
                              setNewComponentColor(component.color);
                              onStartQuickCount();
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: idx < filteredComponents.length - 1 ? '1px solid var(--border-color)' : 'none',
                            transition: 'all 0.2s',
                            background: countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-light)' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-medium)' : 'var(--bg-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = countingModalMode === 'auto' && selectedComponentsForBatch.has(selectionKey) ? 'var(--blue-tint-light)' : 'transparent';
                          }}
                        >
                          {countingModalMode === 'auto' && (
                            <input
                              type="checkbox"
                              checked={selectedComponentsForBatch.has(selectionKey)}
                              readOnly
                              style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                            />
                          )}

                          {component.symbolType === 'Lighting' ? (
                            <div style={{
                              minWidth: '36px',
                              height: '36px',
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                              border: '2px solid #f59e0b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '13px',
                              color: 'var(--yellow-text-dark)',
                              flexShrink: 0,
                            }}>
                              {component.symbol || '?'}
                            </div>
                          ) : component.referenceImage ? (
                            <img
                              src={component.referenceImage}
                              alt={component.name}
                              style={{
                                width: '36px',
                                height: '36px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: `2px solid ${component.color}`,
                                flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '6px',
                              backgroundColor: component.color,
                              flexShrink: 0,
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              border: '2px solid rgba(0, 0, 0, 0.1)',
                            }} />
                          )}

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name}
                            </span>
                            {component.symbolType === 'Lighting' && (
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', lineHeight: '1.3', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {component.description || component.name}
                              </span>
                            )}
                          </div>

                          {countingModalMode === 'manual' && (
                            <ChevronRight size={16} style={{ color: 'var(--text-placeholder)' }} />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-placeholder)', fontSize: '13px' }}>
                      No components found matching "{quickCountSearch}"
                    </div>
                  )}
                </div>

                {countingModalMode === 'auto' && filteredComponents.length > 0 && (
                  <button
                    onClick={onBatchAutocount}
                    disabled={selectedComponentsForBatch.size === 0}
                    style={{
                      width: '100%',
                      padding: '12px 20px',
                      marginTop: '10px',
                      background: selectedComponentsForBatch.size > 0
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                        : 'var(--border-strong)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: selectedComponentsForBatch.size > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: selectedComponentsForBatch.size > 0
                        ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                        : 'none',
                      transition: 'all 0.2s',
                      opacity: selectedComponentsForBatch.size > 0 ? 1 : 0.6,
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (selectedComponentsForBatch.size > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedComponentsForBatch.size > 0) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                      }
                    }}
                  >
                    <Rocket size={16} />
                    Count ({selectedComponentsForBatch.size})
                  </button>
                )}
              </div>
            ) : null;
          })()}

          {!isAddingNewOnly && (electricalKey?.components?.length || 0) > 0 && (
            <div style={{ width: '1px', background: 'var(--border-color)', flexShrink: 0, alignSelf: 'stretch' }} />
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
            {!isAddingNewOnly && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                marginBottom: '12px',
                fontWeight: 500,
              }}>
                <Plus size={14} style={{ color: '#3b82f6' }} />
                Create New
              </label>
            )}

            {isAddingNewOnly && referenceAreaImage && (
              <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px', fontWeight: 500, alignSelf: 'flex-start' }}>
                  Scanned Symbol
                </label>
                <div style={{
                  width: '100%',
                  height: '140px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '12px',
                  background: 'var(--bg-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
                }}>
                  <img
                    src={referenceAreaImage}
                    alt="Scanned symbol"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      background: 'var(--bg-primary)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                  <button
                    onClick={onRetakeAreaSelection}
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Camera size={12} />
                    Retake
                  </button>
                </div>
              </div>
            )}

            {!isAddingNewOnly && (() => {
              const existingNames = new Set((electricalKey?.components || []).map((c: any) => c.name.toLowerCase()));
              const hasSinglePoleSwitch = existingNames.has('single-pole switch') || existingNames.has('switch');
              return !hasSinglePoleSwitch ? (
                <button
                  onClick={async () => {
                    const presetSwitch = {
                      name: 'Single-Pole Switch',
                      color: 'rgba(34, 197, 94, 0.6)',
                      symbol: 'S',
                      componentType: 'Switches',
                      subscript: '',
                      description: 'Standard single-pole toggle switch',
                      referenceImage: '',
                      symbolType: 'Power',
                      pages: [],
                      totalCount: 0,
                    };
                    const currentComponents = electricalKey?.components || [];
                    const updatedKey = {
                      ...(electricalKey || {}),
                      components: [...currentComponents, presetSwitch],
                      extractedAt: (electricalKey as any)?.extractedAt || new Date().toISOString(),
                    };
                    if (onKeyExtracted) {
                      onKeyExtracted(updatedKey);
                    }
                    console.log('✅ Added preset: Single-Pole Switch (S)');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 12px',
                    marginBottom: '12px',
                    background: 'var(--green-tint-light)',
                    border: '1px dashed #86efac',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#22c55e'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    color: 'var(--green-text)',
                    fontWeight: 700,
                    fontSize: '14px',
                    flexShrink: 0,
                  }}>S</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Single-Pole Switch</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-placeholder)' }}>Quick add preset</div>
                  </div>
                </button>
              ) : null;
            })()}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px', fontWeight: 500 }}>
                Component Name
              </label>
              <input
                type="text"
                value={newComponentName}
                onChange={(e) => setNewComponentName(e.target.value)}
                placeholder="e.g., Smoke Detector, Outlet"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newComponentName.trim()) {
                    if (countingModalMode === 'manual') {
                      onStartQuickCount();
                    } else {
                      onStartAreaSelection();
                    }
                  }
                }}
              />
            </div>

            <div>
              {countingModalMode === 'manual' ? (
                <button
                  onClick={() => {
                    if (newComponentName.trim() && !isAddingComponent) {
                      if (referenceAreaImage) {
                        onConfirmAddToKey();
                      } else if (isAddingNewOnly) {
                        onStartAreaSelection();
                      } else {
                        onStartQuickCount();
                      }
                    }
                  }}
                  disabled={!newComponentName.trim() || isAddingComponent}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: newComponentName.trim() && !isAddingComponent ? '#3b82f6' : 'var(--border-strong)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: newComponentName.trim() && !isAddingComponent ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (newComponentName.trim() && !isAddingComponent) {
                      e.currentTarget.style.background = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (newComponentName.trim() && !isAddingComponent) {
                      e.currentTarget.style.background = '#3b82f6';
                    }
                  }}
                >
                  {isAddingComponent ? 'Adding...' : (referenceAreaImage ? 'Add to Key' : (isAddingNewOnly ? 'Scan Symbol' : 'Start Counting'))}
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (newComponentName.trim()) {
                      onStartAreaSelection();
                    }
                  }}
                  disabled={!newComponentName.trim()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: newComponentName.trim() ? '#3b82f6' : 'var(--border-strong)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: newComponentName.trim() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (newComponentName.trim()) {
                      e.currentTarget.style.background = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (newComponentName.trim()) {
                      e.currentTarget.style.background = '#3b82f6';
                    }
                  }}
                >
                  Select Area
                </button>
              )}
              <div style={{
                marginTop: '10px',
                fontSize: '12px',
                color: 'var(--text-muted)',
                textAlign: 'center',
                lineHeight: '1.4',
              }}>
                {isAddingNewOnly ? (
                  referenceAreaImage
                    ? 'Click "Add to Key" to save this component with its symbol'
                    : 'Enter a name and click "Scan" to select the symbol from the PDF'
                ) : (
                  countingModalMode === 'manual'
                    ? (referenceAreaImage ? 'Click "Add to Key" to save this component with its symbol' : 'Click to place each component on the canvas')
                    : 'Select an area with one example to auto-detect all similar symbols'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
